import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IUser } from '@core/interfaces/user.interface';
import { UserService } from '@core/services/user.service';
import { ComponentStore } from '@ngrx/component-store';
import { EBugReportStatus, EBugReportType, IBugReport } from '@shared/components/bug-report/bug-report.interface';
import { upperFirst } from 'lodash';
import { catchError, EMPTY, Observable, switchMap, tap } from 'rxjs';
import { AdminUserFeedbackService } from './admin-user-feedback.service';

interface IAdminUserFeedbackState {
  userFeedback: IBugReport[] | null;
  users: Map<string, IUser> | null;
  loading: boolean;
  error: string | null;
}

const initialState: IAdminUserFeedbackState = {
  error: null,
  loading: false,
  userFeedback: null,
  users: null,
};

@Injectable()
export class AdminUserFeedbackStore extends ComponentStore<IAdminUserFeedbackState> {
  public readonly userFeedback$ = this.selectSignal((state: IAdminUserFeedbackState) => state.userFeedback);
  public readonly loading$ = this.selectSignal((state: IAdminUserFeedbackState) => state.loading);
  public readonly error$ = this.selectSignal((state: IAdminUserFeedbackState) => state.error);
  public readonly users$ = this.selectSignal((state: IAdminUserFeedbackState) => state.users);

  public readonly fetchUserFeedBackList = this.effect(
    (params$: Observable<{ types?: EBugReportType[]; statuses?: EBugReportStatus[]; userGroupIds?: string[] }>) => {
      return params$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ types, statuses, userGroupIds }) => {
          let params = new HttpParams().set('limit', '1000').set('order_by', '-created_at');

          if (types?.length) {
            params = params.set('type__in', types.map((type) => upperFirst(type)).join(','));
          }

          if (statuses?.length) {
            params = params.set('status__in', statuses.map((status) => upperFirst(status)).join(','));
          }

          if (userGroupIds?.length) {
            params = params.set('user_group_id__in', userGroupIds?.join(','));
          }

          return this.adminUserFeedbackService.getUserFeedbackList(params).pipe(
            tap((userFeedback: IApiResponse<IBugReport[]>) => {
              this.patchState({ loading: false, userFeedback: userFeedback.data ?? [] });
            }),
            catchError(() => this.handleError()),
          );
        }),
      );
    },
  );

  public readonly updateUserFeedbackStatus = this.effect(
    (params$: Observable<{ id: string; status: EBugReportStatus }>) => {
      return params$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ id, status }) => {
          return this.adminUserFeedbackService.updateUserFeedbackStatus(id, status).pipe(
            tap((userFeedback: IBugReport) => {
              this.patchState({
                loading: false,
                userFeedback:
                  this.get().userFeedback?.map((feedback) => (feedback.id === id ? userFeedback : feedback)) ?? [],
              });
            }),
            catchError(() => this.handleError()),
          );
        }),
      );
    },
  );

  public readonly fetchCreators = this.effect((params$: Observable<void>) => {
    return params$.pipe(
      tap(() => this.patchState({ error: null })),
      switchMap(() => {
        return this.userService.getUsersAdmin(new HttpParams().set('limit', '1000')).pipe(
          tap((users: IApiResponse<IUser[]>) => {
            this.patchState({
              users: new Map(users.data?.map((user) => [user.id, user]) ?? []),
            });
          }),
          catchError(() => {
            this.patchState({ error: 'Failed to load user feedback' });

            return EMPTY;
          }),
        );
      }),
    );
  });

  private handleError(): Observable<never> {
    this.patchState({
      error: 'Failed to load user feedback',
      loading: false,
    });

    return EMPTY;
  }

  constructor(
    private readonly adminUserFeedbackService: AdminUserFeedbackService,
    private readonly userService: UserService,
  ) {
    super(initialState);
  }
}
